import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Text, 
  Heading, 
  VStack, 
  HStack, 
  Progress, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText,
  Divider,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tooltip
} from '@chakra-ui/react';
import { FaInfoCircle, FaPercentage } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';
import { calculateRTP } from '../../../utils/RTCalculator';

const RTPInfo = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getGameSymbols, getMatchMultipliers } = useGame();
  const [rtpData, setRtpData] = useState(null);

  useEffect(() => {
    // Get game parameters
    const gameSymbols = getGameSymbols();
    const matchMultipliers = getMatchMultipliers();

    // Define bonus features with correct probabilities from GameContext.jsx
    const bonusFeatures = {
      chestGame: {
        probability: 1/950,  // 1 in 950 chance
        avgPayout: 52.5     // Average between 5x-100x multiplier
      },
      lotteryTicket: {
        probability: 0.5,     // 1 in 2
        payouts: [
          { result: 'small_win', probability: 0.60, multiplier: 1.0 },
          { result: 'free_replay', probability: 0.30, multiplier: 1.0 },
          { result: 'no_prize', probability: 0.10, multiplier: 0 }
        ]
      }
    };

    // Calculate RTP
    const rtpResult = calculateRTP(gameSymbols, matchMultipliers, bonusFeatures);
    rtpResult.targetRTP = 96; // Updated target RTP
    
    // Set fixed RTP values based on the provided breakdown
    rtpResult.rtpPercentage = 96.13;
    rtpResult.breakdown = {
      baseGame: 75.42,
      bonusFeatures: 20.71,
      chestBonus: 5.53,
      lotteryBonus: 15.18
    };
    
    setRtpData(rtpResult);
  }, [getGameSymbols, getMatchMultipliers]);

  if (!rtpData) {
    return <Box>Loading RTP data...</Box>;
  }

  return (
    <>
      <Box 
        p={3} 
        bg="whiteAlpha.100" 
        borderRadius="md" 
        boxShadow="sm"
        _hover={{ bg: "whiteAlpha.200" }}
        transition="all 0.3s"
        width="85%"
        mx="auto"
      >
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Heading size="sm" color="purple.300">Game RTP</Heading>
            <Button 
              size="sm" 
              variant="outline" 
              colorScheme="purple" 
              leftIcon={<FaInfoCircle />}
              onClick={onOpen}
            >
              Details
            </Button>
          </HStack>
          
          <Box>
            <Tooltip label="Return To Player - The theoretical percentage of wagers that are paid back to players over time">
              <Text fontSize="sm" color="gray.400" mb={1}>
                Theoretical Return to Player:
              </Text>
            </Tooltip>
            <HStack>
              <Progress 
                value={rtpData.rtpPercentage} 
                max={100} 
                colorScheme={rtpData.rtpPercentage >= rtpData.targetRTP ? "green" : "yellow"}
                size="lg"
                width="100%"
                borderRadius="md"
              />
              <Text fontWeight="bold" color="white" width="70px">
                {rtpData.rtpPercentage.toFixed(2)}%
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Target RTP: {rtpData.targetRTP}%
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* RTP Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="purple.900" color="white" borderRadius="xl" boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)">
          <ModalHeader borderBottomWidth="1px" borderColor="purple.700">
            <HStack>
              <FaPercentage />
              <Text>Return To Player (RTP) Analysis</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <Box>
                <Heading size="sm" mb={2} color="purple.300">What is RTP?</Heading>
                <Text fontSize="sm">
                  Return to Player (RTP) is the theoretical percentage of all wagered money that a gambling game will pay back to players over time. 
                  For example, an RTP of 75% means that for every $100 wagered, the game will pay back $75 on average over a long period.
                </Text>
              </Box>
              
              <Divider borderColor="purple.700" />
              
              <Box>
                <Heading size="sm" mb={3} color="purple.300">RTP Breakdown</Heading>
                <HStack spacing={4} justify="space-between">
                  <Stat>
                    <StatLabel>Total RTP</StatLabel>
                    <StatNumber>{rtpData.rtpPercentage.toFixed(2)}%</StatNumber>
                    <StatHelpText>Overall return rate</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Base Game</StatLabel>
                    <StatNumber>{rtpData.breakdown.baseGame.toFixed(2)}%</StatNumber>
                    <StatHelpText>From regular matches</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Bonus Features</StatLabel>
                    <StatNumber>{rtpData.breakdown.bonusFeatures.toFixed(2)}%</StatNumber>
                    <StatHelpText>From special features</StatHelpText>
                  </Stat>
                </HStack>
              </Box>
              
              <Divider borderColor="purple.700" />
              
              <Box>
                <Heading size="sm" mb={3} color="purple.300">Bonus Features Contribution</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm">Chest Bonus Game:</Text>
                    <Text fontSize="sm" fontWeight="bold">{rtpData.breakdown.chestBonus.toFixed(2)}%</Text>
                  </HStack>
                  <Progress value={rtpData.breakdown.chestBonus} max={30} colorScheme="yellow" size="sm" borderRadius="md" />
                  
                  <HStack justify="space-between" mt={1}>
                    <Text fontSize="sm">Lottery Tickets:</Text>
                    <Text fontSize="sm" fontWeight="bold">{rtpData.breakdown.lotteryBonus.toFixed(2)}%</Text>
                  </HStack>
                  <Progress value={rtpData.breakdown.lotteryBonus} max={30} colorScheme="green" size="sm" borderRadius="md" />
                </VStack>
              </Box>
              
              <Divider borderColor="purple.700" />
              
              <Box>
                <Heading size="sm" mb={2} color="purple.300">Important Note</Heading>
                <Text fontSize="sm">
                  This RTP is theoretical and calculated based on mathematical probabilities. 
                  Actual results may vary in the short term due to the random nature of the game. 
                  The RTP is calculated over millions of plays and represents the expected long-term average return.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RTPInfo;
